/**
 * Akademik yil secimi.
 *
 * Uygulamada yuzlerce fetch cagrisi var; her birine tek tek baslik eklemek yerine
 * window.fetch bir kez sarmalanir ve secili yil X-Academic-Year olarak eklenir.
 * Backend bu basligi okuyup veritabani oturumuna yazar, courses/course_info view'lari
 * da ona gore filtreler. Boylece mevcut istek kodlarinin hicbiri degismez
 * (api.js'teki apiFetch dahil - o da window.fetch uzerinden gider).
 *
 * Sinav/odev/soru kayitlari derse course_id ile bagli oldugu icin yil izolasyonu
 * kendiliginden olusur: gecmis yilin sinavlari yeni yil secilikken JOIN'de duser,
 * yil geri alininca aynen geri gelir.
 *
 * Gecmis yil salt-okunurdur; bu kural veritabaninda trigger ile zorlanir.
 * Buradaki isReadOnly() yalnizca arayuzde rozet/kilit gostermek icindir.
 */

import axios from 'axios';

const STORAGE_KEY = 'academicYear';
const CURRENT_STORAGE_KEY = 'academicYearCurrent';
const EXPLICIT_SELECTION_KEY = 'academicYearExplicitSelection';
export const ACADEMIC_YEAR_POLL_INTERVAL_MS = 30000;

let currentYearMeta = null; // { year, label, is_current } - /academic-years'ten

export function getSelectedYear() {
  return sessionStorage.getItem(EXPLICIT_SELECTION_KEY)
    || localStorage.getItem(CURRENT_STORAGE_KEY)
    || '';
}

export function setSelectedYear(year) {
  if (year) {
    localStorage.setItem(STORAGE_KEY, year);
    sessionStorage.setItem(EXPLICIT_SELECTION_KEY, year);
  } else {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(EXPLICIT_SELECTION_KEY);
  }
  // Secili yil tum ekranlarin verisini degistirir; en guvenli yol tam yenileme.
  window.location.reload();
}

export function setYearMeta(meta) {
  currentYearMeta = meta;
  document.body.classList.toggle('academic-year-readonly', Boolean(meta && !meta.editable));
  window.dispatchEvent(new CustomEvent('academic-year-meta-changed', { detail: meta }));
}

export function reconcileAcademicYears(data) {
  if (!Array.isArray(data)) return null;

  const current = data.find((item) => item.is_current && item.status === 'active')
    || data.find((item) => item.is_current)
    || null;
  const previousCurrent = localStorage.getItem(CURRENT_STORAGE_KEY) || '';
  const explicitSelection = sessionStorage.getItem(EXPLICIT_SELECTION_KEY) || '';
  const stored = getSelectedYear();
  const currentChanged = Boolean(
    current?.year && previousCurrent && previousCurrent !== current.year
  );
  const explicitMeta = data.find((item) => item.year === explicitSelection);
  const preserveExplicitArchive = Boolean(
    explicitMeta
    && explicitMeta.status === 'archived'
    && explicitSelection !== previousCurrent
  );
  const selected = preserveExplicitArchive
    ? explicitSelection
    : (current?.year || '');

  if (current?.year) localStorage.setItem(CURRENT_STORAGE_KEY, current.year);
  if (selected) localStorage.setItem(STORAGE_KEY, selected);
  else localStorage.removeItem(STORAGE_KEY);
  if (!preserveExplicitArchive) {
    sessionStorage.removeItem(EXPLICIT_SELECTION_KEY);
  }

  const selectedMeta = data.find((item) => item.year === selected) || current;
  setYearMeta(selectedMeta || null);

  return {
    years: data,
    current,
    selected,
    currentChanged,
    selectionChanged: selected !== stored,
  };
}

export function startAcademicYearMonitor(loadYears, onUpdate, intervalMs = ACADEMIC_YEAR_POLL_INTERVAL_MS) {
  let stopped = false;
  let requestInFlight = false;

  const refresh = async () => {
    if (stopped || requestInFlight) return;
    requestInFlight = true;
    try {
      const data = await loadYears();
      const result = reconcileAcademicYears(data);
      if (!stopped && result) onUpdate(result);
    } catch {
      // Gecici ag hatalarinda mevcut yil secimi korunur; sonraki tur yeniden dener.
    } finally {
      requestInFlight = false;
    }
  };

  const refreshWhenVisible = () => {
    if (document.visibilityState === 'visible') refresh();
  };

  refresh();
  const timerId = window.setInterval(refresh, intervalMs);
  document.addEventListener('visibilitychange', refreshWhenVisible);
  window.addEventListener('focus', refresh);

  return () => {
    stopped = true;
    window.clearInterval(timerId);
    document.removeEventListener('visibilitychange', refreshWhenVisible);
    window.removeEventListener('focus', refresh);
  };
}

export function announceAcademicYearChange(result) {
  if (!result?.currentChanged || !result.current) return false;
  window.alert(
    `Akademik yıl ${result.current.label || result.current.year} olarak güncellendi.`
  );
  return result.selectionChanged;
}

/** Secili yil duzenlenebilir mi (yalnizca guncel yil duzenlenebilir). */
export function isReadOnly() {
  if (!currentYearMeta) return false;
  return !currentYearMeta.editable;
}

function isStudentSession() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    return user?.userType === 'student' || Boolean(user?.student_number);
  } catch {
    return false;
  }
}

function isNonAcademicYearWrite(input) {
  try {
    const url = input instanceof Request ? input.url : input;
    const path = new URL(url, window.location.origin).pathname;
    return ['/api/login', '/api/forgot-password', '/api/reset-password'].includes(path);
  } catch {
    return false;
  }
}

/** window.fetch'i bir kez sarmala: secili yili her istege ekle. */
export function installAcademicYearHeader() {
  if (!window.__academicYearFetchPatched) {
    window.__academicYearFetchPatched = true;
    const originalFetch = window.fetch.bind(window);

    window.fetch = (input, init = {}) => {
      const year = isStudentSession() ? '' : getSelectedYear();
      const method = (init.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
      if (isReadOnly() && !['GET', 'HEAD', 'OPTIONS'].includes(method)
          && !isNonAcademicYearWrite(input)) {
        return Promise.reject(new Error('Arşiv akademik yılında değişiklik yapılamaz.'));
      }
      if (!year) return originalFetch(input, init);

      const headers = new Headers(
        (init && init.headers) || (input instanceof Request ? input.headers : undefined)
      );
      headers.set('X-Academic-Year', year);

      if (input instanceof Request) return originalFetch(new Request(input, { headers }));
      return originalFetch(input, { ...init, headers });
    };
  }

  if (!window.__academicYearAxiosPatched) {
    window.__academicYearAxiosPatched = true;
    axios.interceptors.request.use((config) => {
      const method = (config.method || 'get').toLowerCase();
      if (isReadOnly() && !['get', 'head', 'options'].includes(method)
          && !isNonAcademicYearWrite(config.url || '')) {
        return Promise.reject(new Error('Arşiv akademik yılında değişiklik yapılamaz.'));
      }
      const year = isStudentSession() ? '' : getSelectedYear();
      if (year) {
        config.headers = config.headers || {};
        config.headers['X-Academic-Year'] = year;
      }
      return config;
    });
  }
}
